import React, { useEffect, useState } from 'react';
import { useApp } from '../App';
import TabBar from '../components/ui/TabBar';
import {
  listenAssignments, listenCourses, upsertCourse,
  upsertAssignments, updateAssignmentAsOwner,
  saveSchoolCalendar, fetchSchoolCalendar,
} from './api';
import { generateYearPlanAssignments } from './seed/yearPlanSeed';
import { buildSchoolCalendar } from './seed/calendar';
import { COURSES } from './seed/courses';
import { syncAnchorsToCalendar } from './gcalSync';
import { listCalendars } from '../lib/gcal';
import ImportTab from './ImportTab';
import AuthorTab from './AuthorTab';
import CoverageTab from './CoverageTab';
import TrendsTab from './TrendsTab';
import WindowsTab from './WindowsTab';
import AttendanceTab from './AttendanceTab';
import MilestonesTab from './MilestonesTab';
import { SchoolDataProvider } from './SchoolDataProvider';
import { StatusChips } from './StatusBanner';
import WeekBoard from './WeekBoard';
import DashboardTab from './DashboardTab';
import './dayBoardTheme.css';

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'setup', label: 'Setup' },
  { id: 'author', label: 'Add Work' },
  { id: 'board', label: 'Board' },
  { id: 'windows', label: 'Windows' },
  { id: 'coverage', label: 'Coverage' },
  { id: 'trends', label: 'Trends' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'milestones', label: 'Milestones' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'courseMap', label: 'Course Map' },
  { id: 'import', label: 'Import' },
];

// Owner's view of the school module — reached from Home's nav grid (only
// shown once a child's schoolType is 'homeschool', see EditChildren.jsx).
export default function OwnerSchoolModule({ onBack }) {
  const { familyData, currentUser } = useApp() || {};
  const [tab, setTab] = useState('dashboard');
  const students = (familyData?.children || []).filter(c => c.schoolType === 'homeschool' && c.studentEmail);
  const [activeStudentId, setActiveStudentId] = useState(students[0]?.id || null);
  const activeStudent = students.find(s => s.id === activeStudentId) || students[0];

  if (students.length === 0) {
    return (
      <div className="module-screen day-board-theme">
        <div className="module-header">
          <button className="btn btn--back" onClick={onBack}>← Home</button>
          <h1>School</h1>
        </div>
        <p className="settings-note" style={{ padding: '0 20px' }}>
          No homeschool student set up yet. Go to Settings → Family Setup → Children,
          edit a child, and set Schooling to "Homeschool".
        </p>
      </div>
    );
  }

  return (
    <div className="module-screen day-board-theme">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Home</button>
        <h1>School</h1>
      </div>
      {students.length > 1 && (
        <div style={{ padding: '0 20px 8px' }}>
          <select className="input" value={activeStudentId} onChange={e => setActiveStudentId(e.target.value)}>
            {students.map(s => <option key={s.id} value={s.id}>{s.preferred_name || s.firstName}</option>)}
          </select>
        </div>
      )}
      <TabBar tabs={TABS} value={tab} onChange={setTab} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
        <div className="school-content">
          {tab === 'dashboard' && <DashboardTab familyData={familyData} student={activeStudent} onNavigate={setTab} />}
          {tab === 'setup' && <SetupTab familyData={familyData} student={activeStudent} />}
          {tab === 'author' && <AuthorTab familyData={familyData} student={activeStudent} />}
          {tab === 'board' && (
            // key= is belt-and-braces on top of the provider's own effect deps:
            // switching student in the picker can't leak the previous one's rows.
            // No router here, so StatusChips gets no onNavigate and renders as
            // plain text rather than tappable chips.
            <SchoolDataProvider student={activeStudent} key={activeStudent?.id}>
              <div style={{ paddingTop: 8 }}>
                <p className="db-item-sub" style={{ marginBottom: 8 }}>This is exactly what {activeStudent?.preferred_name || 'the student'} sees.</p>
                <StatusChips />
                <WeekBoard />
              </div>
            </SchoolDataProvider>
          )}
          {tab === 'windows' && <WindowsTab familyData={familyData} />}
          {tab === 'coverage' && <CoverageTab familyData={familyData} student={activeStudent} />}
          {tab === 'trends' && <TrendsTab familyData={familyData} student={activeStudent} />}
          {tab === 'attendance' && <AttendanceTab familyData={familyData} student={activeStudent} />}
          {tab === 'milestones' && <MilestonesTab familyData={familyData} />}
          {tab === 'assignments' && <AssignmentsTab familyData={familyData} student={activeStudent} />}
          {tab === 'courseMap' && <CourseMapTab familyData={familyData} student={activeStudent} />}
          {tab === 'import' && <ImportTab familyData={familyData} student={activeStudent} currentUserEmail={currentUser?.email} />}
        </div>
      </div>
    </div>
  );
}

// ─── SETUP ────────────────────────────────────────────────────────────────────

function SetupTab({ familyData, student }) {
  const [calendar, setCalendar] = useState(undefined);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [gcalEnabled, setGcalEnabled] = useState(false);
  const [gcalCalendars, setGcalCalendars] = useState(null);
  const [selectedCalId, setSelectedCalId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    if (!familyData?.id) return;
    fetchSchoolCalendar(familyData.id).then(cal => {
      setCalendar(cal);
      if (cal?.gcalCalendarId) {
        setGcalEnabled(true);
        setSelectedCalId(cal.gcalCalendarId);
      }
    });
  }, [familyData?.id]);

  useEffect(() => {
    if (!gcalEnabled || gcalCalendars) return;
    listCalendars().then(setGcalCalendars).catch(() => setGcalCalendars([]));
  }, [gcalEnabled, gcalCalendars]);

  async function runSync() {
    if (!familyData?.id || !selectedCalId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      await syncAnchorsToCalendar(familyData.id, selectedCalId);
      setSyncResult('Tutor, swim, and co-op are synced to that calendar.');
    } catch (err) {
      console.error('[school] gcal sync failed:', err);
      setSyncResult('Sync failed — check the console (may need to reconnect Google Calendar).');
    } finally {
      setSyncing(false);
    }
  }

  async function runSeed() {
    if (!familyData?.id || !student?.studentEmail) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      // Courses first (assignments reference courseId) — cheap to re-run,
      // upsertCourse merges so hand-edited names/colors aren't clobbered
      // on a re-seed... actually this overwrites; only run once per course id
      // that hasn't been customized. Fine for first-time setup.
      for (const c of COURSES) await upsertCourse(familyData.id, c);
      const schoolCal = buildSchoolCalendar();
      await saveSchoolCalendar(familyData.id, schoolCal);
      const assignments = generateYearPlanAssignments(student.studentEmail);
      await upsertAssignments(familyData.id, assignments);
      setCalendar(schoolCal);
      setSeedResult(`Seeded ${assignments.length} assignments across ${schoolCal.totalInstructionalDays} instructional days.`);
    } catch (err) {
      console.error('[school] seed failed:', err);
      setSeedResult('Seed failed — check the console.');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div>
      <p className="settings-note" style={{ marginTop: 12 }}>
        {calendar?.weeks?.length
          ? `Year plan already seeded (${calendar.totalInstructionalDays} instructional days, ${calendar.startDate} → ${calendar.endDate}).`
          : 'No year plan seeded yet for this family.'}
      </p>
      <button className="btn btn--primary" onClick={runSeed} disabled={seeding}>
        {seeding ? 'Seeding…' : calendar?.weeks?.length ? 'Re-seed year plan' : 'Seed year plan'}
      </button>
      {seedResult && <p className="settings-note" style={{ marginTop: 8 }}>{seedResult}</p>}
      <p className="settings-note" style={{ marginTop: 16 }}>
        Re-seeding is safe — assignment ids are deterministic ({'{date}__{block}__{item}'}),
        so this upserts rather than duplicating. It does not touch anything a
        student has already marked complete or flagged unless the underlying
        item's core fields (title, block, order) changed.
      </p>

      <div className="form-label" style={{ marginTop: 20 }}>Google Calendar</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={gcalEnabled} onChange={e => setGcalEnabled(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Sync tutor/swim/co-op to Google Calendar</span>
      </label>
      <p className="settings-note">
        Only these three fixed commitments sync — the daily six and weekly-board tiles
        stay inside the app, on purpose (they're self-paced, not clock events).
      </p>
      {gcalEnabled && (
        <>
          <select className="input" value={selectedCalId} onChange={e => setSelectedCalId(e.target.value)}>
            <option value="">— choose a calendar —</option>
            {(gcalCalendars || []).map(c => <option key={c.id} value={c.id}>{c.summary}</option>)}
          </select>
          <button className="btn btn--secondary" style={{ marginTop: 8 }} disabled={!selectedCalId || syncing} onClick={runSync}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          {syncResult && <p className="settings-note" style={{ marginTop: 8 }}>{syncResult}</p>}
        </>
      )}
    </div>
  );
}

// ─── ASSIGNMENTS ──────────────────────────────────────────────────────────────

function AssignmentsTab({ familyData, student }) {
  const [assignments, setAssignments] = useState(null);

  useEffect(() => {
    if (!familyData?.id || !student?.studentEmail) return;
    return listenAssignments(familyData.id, student.studentEmail, setAssignments);
  }, [familyData?.id, student?.studentEmail]);

  if (assignments === null) return <p className="settings-note">Loading…</p>;
  if (assignments.length === 0) return <p className="settings-note">No assignments yet — seed the year plan first.</p>;

  const flagged = assignments.filter(a => a.status === 'flagged' || a.confusionFlag);
  const completed = assignments.filter(a => a.status === 'complete');
  // Organized by reviewable or not (gap-review item 7) — needsReview defaults
  // true for older docs that predate the field (seed-generated ones included).
  const needsReview = completed.filter(a => a.needsReview !== false && !a.reviewedByParent);
  const noReviewNeeded = completed.filter(a => a.needsReview === false);
  // (a.date || '') matters: unplaced weekly-board tiles have date === null and
  // are 'not_started', so they always reach this sort — a bare
  // a.date.localeCompare threw and took the whole tab down with it.
  const upcoming = assignments
    .filter(a => a.status !== 'flagged' && a.status !== 'complete' && !a.confusionFlag)
    .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 30); // a full 1,400-doc list isn't useful to scroll

  function markReviewed(a) {
    updateAssignmentAsOwner(familyData.id, a.id, { reviewedByParent: true });
  }

  return (
    <div>
      {flagged.length > 0 && (
        <>
          <div className="form-label" style={{ marginTop: 0 }}>Flagged — needs attention ({flagged.length})</div>
          <div className="edit-list">
            {flagged.map(a => <AssignmentRow key={a.id} a={a} onReview={() => markReviewed(a)} />)}
          </div>
        </>
      )}
      {needsReview.length > 0 && (
        <>
          <div className="form-label" style={{ marginTop: 16 }}>Completed — needs a look ({needsReview.length})</div>
          <div className="edit-list">
            {needsReview.map(a => <AssignmentRow key={a.id} a={a} onReview={() => markReviewed(a)} />)}
          </div>
        </>
      )}
      {noReviewNeeded.length > 0 && (
        <>
          <div className="form-label" style={{ marginTop: 16 }}>Completed — no review needed ({noReviewNeeded.length})</div>
          <div className="edit-list">
            {noReviewNeeded.slice(0, 15).map(a => <AssignmentRow key={a.id} a={a} />)}
          </div>
        </>
      )}
      <div className="form-label" style={{ marginTop: 16 }}>Upcoming / in progress</div>
      <div className="edit-list">
        {upcoming.map(a => <AssignmentRow key={a.id} a={a} />)}
      </div>
    </div>
  );
}

function AssignmentRow({ a, onReview }) {
  return (
    <div className="edit-list-row">
      <div className="edit-list-row__info">
        <span className="edit-list-row__id-badge">{a.title[0]}</span>
        <div>
          <div className="edit-list-row__name">{a.title}</div>
          <div className="edit-list-row__sub">
            {[a.date || 'no day yet', a.status, a.studentQuestion].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      {onReview && !a.reviewedByParent && a.status === 'complete' && a.needsReview !== false && (
        <button className="btn btn--ghost btn--small" onClick={onReview}>Mark reviewed</button>
      )}
    </div>
  );
}

// ─── COURSE MAP ───────────────────────────────────────────────────────────────

function CourseMapTab({ familyData, student }) {
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState(null);

  useEffect(() => {
    if (!familyData?.id) return;
    const unsubC = listenCourses(familyData.id, setCourses);
    if (!student?.studentEmail) {
      setAssignments([]);
      return unsubC;
    }
    const unsubA = listenAssignments(familyData.id, student.studentEmail, setAssignments);
    return () => { unsubC(); unsubA(); };
  }, [familyData?.id, student?.studentEmail]);

  if (assignments === null) return <p className="settings-note">Loading…</p>;

  const countByCourse = {};
  for (const a of assignments) countByCourse[a.courseId] = (countByCourse[a.courseId] || 0) + 1;

  return (
    <div className="edit-list">
      {courses.map(c => (
        <div key={c.id} className="edit-list-row">
          <div className="edit-list-row__info">
            <span className="edit-list-row__id-badge" style={{ background: `var(${c.colorToken})` }}>
              {c.name[0]}
            </span>
            <div>
              <div className="edit-list-row__name">{c.name}</div>
              <div className="edit-list-row__sub">{c.subjectArea} · {countByCourse[c.id] || 0} assignments</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
